import { theme } from '../../DeviceSchedule/constants';
import { RefreshIcon, SearchIcon } from '../../DeviceSchedule/components/Icons';
import { deviceKey, formatTime, fromNow, humanizeReason, statusOf } from '../function';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'online', label: 'Online' },
  { key: 'offline', label: 'Offline' },
];

const STATUS_STYLE = {
  online: { label: 'Online', color: '#059669', bg: 'rgba(16,185,129,0.10)', dot: '#10b981' },
  offline: { label: 'Offline', color: '#dc2626', bg: 'rgba(239,68,68,0.10)', dot: '#ef4444' },
  unknown: { label: 'Unknown', color: '#6b7280', bg: '#f3f4f6', dot: '#9ca3af' },
};

const EmptyState = ({ title, subtitle }) => (
  <div style={{ padding: '52px 20px', textAlign: 'center' }}>
    <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.textMuted }}>{title}</div>
    {subtitle && <div style={{ fontSize: 12.5, color: theme.textFaint, marginTop: 5 }}>{subtitle}</div>}
  </div>
);

const Skeletons = () => (
  <div style={{ padding: '14px 2px', display: 'flex', flexDirection: 'column', gap: 10 }}>
    {[0, 1, 2, 3, 4].map((row) => (
      <div key={row} className="dsSkeleton" style={{ height: 44, opacity: 1 - row * 0.15 }} />
    ))}
  </div>
);

const headCell = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.textFaint,
  padding: '0 12px 10px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const bodyCell = { padding: '10px 12px', verticalAlign: 'middle' };

function StatusCell({ entry }) {
  if (entry?.loading) {
    return <div className="dsSkeleton" style={{ height: 22, width: 80 }} />;
  }
  const status = statusOf(entry);
  const style = STATUS_STYLE[status];

  return (
    <div>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 10px',
          borderRadius: 999,
          background: style.bg,
          color: style.color,
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: style.dot }} />
        {style.label}
      </span>
      {entry?.error && <div style={{ fontSize: 11, color: theme.danger, marginTop: 4 }}>{entry.error}</div>}
      {status === 'online' && entry.connectedSince && (
        <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 4 }}>
          since {formatTime(entry.connectedSince)}
        </div>
      )}
    </div>
  );
}

function DisconnectCell({ entry }) {
  if (entry?.loading) {
    return <div className="dsSkeleton" style={{ height: 18, width: 140 }} />;
  }
  if (!entry?.disconnectTime) {
    return <span style={{ fontSize: 13, color: theme.textFaint }}>—</span>;
  }

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{formatTime(entry.disconnectTime)}</div>
      <div style={{ fontSize: 11, color: theme.textFaint, marginTop: 3 }}>
        {fromNow(entry.disconnectTime)}
        {entry.disconnectReason ? ` · ${humanizeReason(entry.disconnectReason)}` : ''}
      </div>
    </div>
  );
}

/** DeviceID / Name / Room / Connection Status / Disconnected At for a property. */
function ConnectionStatusTable({
  devices,
  total,
  counts,
  loading,
  hasHouse,
  statuses,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  onRefreshDevice,
}) {
  const renderBody = () => {
    if (!hasHouse) {
      return <EmptyState title="Select a property to begin" subtitle="Its devices will be listed here." />;
    }
    if (loading) return <Skeletons />;
    if (!total) {
      return <EmptyState title="No devices on this property" subtitle="Choose a different property." />;
    }
    if (!devices.length) {
      return <EmptyState title="No devices match" subtitle="Try a different search or filter." />;
    }

    return (
      <div className="dsScroll" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 820 }}>
          <thead>
            <tr>
              <th style={headCell}>DeviceID</th>
              <th style={headCell}>Name</th>
              <th style={headCell}>Room Name</th>
              <th style={headCell}>Connection Status</th>
              <th style={headCell}>Disconnected At</th>
              <th style={{ ...headCell, textAlign: 'right' }}>Refresh</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => {
              const entry = statuses[device.deviceId];
              return (
                <tr key={deviceKey(device)} className="dsRow">
                  <td style={bodyCell}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: theme.text, fontFamily: 'monospace' }}>
                      {device.deviceId}
                    </span>
                  </td>
                  <td style={bodyCell}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: device.hasName ? theme.text : theme.textFaint }}>
                      {device.hasName ? device.name : 'Unnamed device'}
                    </span>
                  </td>
                  <td style={bodyCell}>
                    <span style={{ fontSize: 13, color: theme.textMuted }}>{device.roomName}</span>
                  </td>
                  <td style={bodyCell}>
                    <StatusCell entry={entry} />
                  </td>
                  <td style={bodyCell}>
                    <DisconnectCell entry={entry} />
                  </td>
                  <td style={{ ...bodyCell, textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => onRefreshDevice(device.deviceId)}
                      disabled={entry?.loading}
                      title="Refresh this device"
                      style={{
                        border: `1px solid ${theme.border}`,
                        background: theme.surface,
                        color: theme.textMuted,
                        borderRadius: 8,
                        width: 30,
                        height: 30,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: entry?.loading ? 'progress' : 'pointer',
                      }}
                    >
                      <span className={entry?.loading ? 'dsSpin' : undefined} style={{ display: 'flex' }}>
                        <RefreshIcon size={13} />
                      </span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        padding: '16px 18px 18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.text, lineHeight: '17px' }}>Devices</div>
          <div style={{ fontSize: 11.5, color: theme.textFaint, lineHeight: '16px' }}>
            {hasHouse ? `${devices.length} of ${total} shown` : 'Waiting for a property'}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {FILTERS.map((item) => {
              const active = filter === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => onFilterChange(item.key)}
                  disabled={!hasHouse}
                  style={{
                    border: 'none',
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: theme.font,
                    background: active ? theme.accentSoft : theme.surface,
                    color: active ? theme.accent : theme.textMuted,
                    cursor: hasHouse ? 'pointer' : 'default',
                  }}
                >
                  {item.label} ({counts[item.key]})
                </button>
              );
            })}
          </div>

          <div style={{ position: 'relative' }}>
            <span
              style={{
                position: 'absolute',
                left: 11,
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.textFaint,
                pointerEvents: 'none',
                display: 'flex',
              }}
            >
              <SearchIcon size={14} />
            </span>
            <input
              type="text"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search ID, name or room…"
              disabled={!hasHouse}
              style={{
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: '7px 12px 7px 32px',
                fontSize: 13,
                fontFamily: theme.font,
                outline: 'none',
                width: 250,
                color: '#374151',
                background: hasHouse ? theme.surface : theme.surfaceMuted,
              }}
            />
          </div>
        </div>
      </div>

      {renderBody()}
    </div>
  );
}

export default ConnectionStatusTable;
